// code-guardian-ignore-duplicates: shared presentational scaffolding (bulk-upload modal from the design system); product-master twin of EmployeeCustomFieldsBulkUploadModal
import { useState } from 'react';
import { Download, FileSpreadsheet, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import Modal from './Modal';
import { productMasterAPI } from '../services/api';
import { downloadXlsx } from '../utils/download';
import { UploadErrorBanner } from './UploadErrorBanner';
import FileDropZone from './FileDropZone';

/**
 * Catalogue import for Master Data → Products.
 *
 * The thing an admin has to understand about this sheet, and the reason the
 * instructions lead with it: the trailing columns are NOT a fixed schema.
 * Whatever you name them becomes a characteristic of that product. An Oura sheet
 * carries Size / Colour / Bluetooth, an Apple sheet carries Storage / Colour, and
 * both can sit in the same file — the importer reads the axes off the header row
 * rather than being told what they are.
 */

interface BulkUploadResult {
  groupsCreated: number;
  productsCreated: number;
  attributesWritten: number;
  skusCreated: number;
  skusUpdated: number;
  failed: number;
  errors: { row: number; product: string; error: string }[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: number;
}

export default function ProductMasterBulkUploadModal({ isOpen, onClose, onSuccess, projectId }: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileSelected = (selectedFile: File) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError('Please upload a valid Excel file (.xlsx, .xls) or CSV file');
      setFile(null);
      return;
    }
    setFile(selectedFile);
    setError(null);
    setUploadResult(null);
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    setError(null);
    try {
      const response = await productMasterAPI.downloadBulkTemplate(projectId);
      downloadXlsx('product_master_template.xlsx', response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to download template');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = (e.target?.result as string).split(',')[1];
          const response = await productMasterAPI.bulkUpload(projectId, base64Data);

          if (response.data.status === 'success') {
            const d = response.data;
            setUploadResult({
              groupsCreated: d.groupsCreated ?? 0,
              productsCreated: d.productsCreated ?? 0,
              attributesWritten: d.attributesWritten ?? 0,
              skusCreated: d.skusCreated ?? 0,
              skusUpdated: d.skusUpdated ?? 0,
              failed: d.failed ?? 0,
              errors: d.errors ?? [],
            });
            // Refresh on any write at all, including a pure update run where
            // nothing was created — the MRPs on screen are stale either way.
            if ((d.skusCreated ?? 0) > 0 || (d.skusUpdated ?? 0) > 0 || (d.productsCreated ?? 0) > 0) {
              onSuccess();
            }
          } else {
            setError(response.data.message || 'Upload failed');
          }
        } catch (err: any) {
          setError(err.response?.data?.message || 'Failed to upload file');
        } finally {
          setIsUploading(false);
        }
      };
      reader.onerror = () => {
        setError('Failed to read file');
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setError('Failed to process file');
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Product Catalogue"
      size="lg"
      footer={
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={handleClose}
          >
            {uploadResult ? 'Close' : 'Cancel'}
          </button>
          {!uploadResult && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={!file || isUploading}
              // text-white belongs to the enabled branch only: left on the base it also sat
              // on the disabled button beside text-gray-500, and Tailwind emits text-white
              // later, so the label stayed white on the grey button.
              className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                file && !isUploading
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'cursor-not-allowed bg-gray-300 text-gray-500'
              }`}
            >
              {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isUploading ? 'Importing...' : 'Import'}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-6">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="h-6 w-6 flex-shrink-0 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-blue-900">Download Template</h4>
              <p className="mt-1 text-sm text-blue-700">
                One row per SKU. The template's Instructions sheet explains every column,
                and shows a ring and a phone side by side so you can see how the same
                sheet carries products that vary in different ways.
              </p>
              <button
                onClick={handleDownloadTemplate}
                disabled={isDownloading}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
              >
                {isDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {isDownloading ? 'Downloading...' : 'Download Template'}
              </button>
            </div>
          </div>
        </div>

        {!uploadResult && (
          <FileDropZone
            inputId="product-master-bulk-upload-file"
            file={file}
            onFile={handleFileSelected}
            onClear={() => setFile(null)}
          />
        )}

        {error && <UploadErrorBanner message={error} />}

        {uploadResult && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
                <CheckCircle className="mx-auto h-6 w-6 text-green-600" />
                <p className="mt-2 text-2xl font-bold text-green-700">{uploadResult.skusCreated}</p>
                <p className="text-xs text-green-600">SKUs Created</p>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-center">
                <FileSpreadsheet className="mx-auto h-6 w-6 text-blue-600" />
                <p className="mt-2 text-2xl font-bold text-blue-700">{uploadResult.skusUpdated}</p>
                <p className="text-xs text-blue-600">SKUs Updated</p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center">
                <AlertCircle className="mx-auto h-6 w-6 text-red-600" />
                <p className="mt-2 text-2xl font-bold text-red-700">{uploadResult.failed}</p>
                <p className="text-xs text-red-600">Failed</p>
              </div>
            </div>

            <p className="text-center text-xs text-gray-500">
              {uploadResult.productsCreated} product{uploadResult.productsCreated === 1 ? '' : 's'} and{' '}
              {uploadResult.groupsCreated} group{uploadResult.groupsCreated === 1 ? '' : 's'} added ·{' '}
              {uploadResult.attributesWritten} characteristic
              {uploadResult.attributesWritten === 1 ? '' : 's'} written
            </p>

            {uploadResult.errors.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <h4 className="text-sm font-semibold text-red-800">Errors</h4>
                <div className="mt-2 max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-red-200">
                        <th className="py-1 text-left text-red-700">Row</th>
                        <th className="py-1 text-left text-red-700">Product</th>
                        <th className="py-1 text-left text-red-700">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.errors.map((err, idx) => (
                        <tr key={idx} className="border-b border-red-100">
                          <td className="py-1 text-red-600">{err.row}</td>
                          <td className="py-1 text-red-600">{err.product}</td>
                          <td className="py-1 text-red-600">{err.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {!uploadResult && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <h4 className="text-sm font-semibold text-gray-900">How the sheet works</h4>
            <ul className="mt-2 space-y-1 text-xs text-gray-600">
              <li>• One row per SKU. Rows sharing a <strong>Product</strong> name become SKUs of that one product</li>
              <li>
                • <strong>Any column beyond the fixed ones becomes a characteristic.</strong> Name it
                after what varies — Size, Colour, Storage, Gauge — and put one value per row
              </li>
              <li>• Different products can use different characteristic columns in the same sheet; blanks are ignored</li>
              <li>• <strong>Group Path</strong> nests with “&gt;” (Apple &gt; iPhones). Missing levels are created</li>
              <li>• Re-uploading is safe: an existing combination has its code, MRP, UOM and barcode updated rather than being duplicated</li>
              <li>• A bad row fails on its own — the rest of the sheet still imports</li>
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
